import { errors as joseErrors, jwtVerify, type JWTPayload, type JWTVerifyGetKey } from "jose";
import { isIdentityToken } from "./identity";
import { sessionJwks } from "./jwks";

/**
 * Client-side verification of the Passport's session JWT (build plan §3): EdDSA over Ed25519, the
 * signature checked against the Passport's JWKS and the expiry against the clock, with jose. The
 * APIs verify again on every request — this is what lets the shell trust `sub` enough to draw the
 * session, and know WHEN to refresh, without a round trip.
 */
export const SESSION_ALGORITHMS: readonly string[] = ["EdDSA"];
/**
 * 🔴 What the Passport's session tokens carry in `typ`, and what this verifier insists on. The Passport
 * signs its OIDC id_tokens with the same key and the same `kid` over a strict superset of these claims,
 * so without the check one read out of a log would draw a signed-in shell for the person it names.
 */
export const SESSION_JWT_TYP = "JWT";
/** Seconds of clock skew tolerated between the Passport's host and this device. */
export const CLOCK_TOLERANCE_SECONDS = 5;

export interface VerifiedSession {
  token: string;
  /** The Passport id — `sub`. */
  userHash: string;
  /** `sid`. */
  sessionId: string;
  /** `amr` — how the person authenticated; the Passport's vocabulary, carried through. */
  amr: string[];
  /** `exp` in milliseconds since the epoch; null when the token carries none. */
  expiresAtMs: number | null;
  /** A display name, when the Passport includes one. Never more: the Passport is the person (D1, D27). */
  displayName: string | null;
}

export type SessionRejection =
  | "malformed"
  | "unsupported_alg"
  | "unknown_kid"
  | "bad_signature"
  | "expired"
  | "invalid_claims"
  | "jwks_unreachable";

export class SessionVerifyError extends Error {
  readonly reason: SessionRejection;

  constructor(reason: SessionRejection, message: string) {
    super(message);
    this.name = "SessionVerifyError";
    this.reason = reason;
  }
}

function rejectionFor(err: unknown): SessionVerifyError {
  if (err instanceof joseErrors.JWTExpired) return new SessionVerifyError("expired", "session has expired");
  if (err instanceof joseErrors.JWSSignatureVerificationFailed) return new SessionVerifyError("bad_signature", "signature does not verify");
  if (err instanceof joseErrors.JOSEAlgNotAllowed) return new SessionVerifyError("unsupported_alg", err.message);
  if (err instanceof joseErrors.JWKSNoMatchingKey || err instanceof joseErrors.JWKSMultipleMatchingKeys) {
    return new SessionVerifyError("unknown_kid", "no key matches the token");
  }
  if (err instanceof joseErrors.JWKSTimeout) return new SessionVerifyError("jwks_unreachable", "signing keys timed out");
  if (err instanceof joseErrors.JWTClaimValidationFailed) return new SessionVerifyError("invalid_claims", err.message);
  if (err instanceof joseErrors.JOSEError) return new SessionVerifyError("malformed", err.message);
  // A failed fetch (offline, connection refused) surfaces as the runtime's own error.
  return new SessionVerifyError("jwks_unreachable", err instanceof Error ? err.message : String(err));
}

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === "string" && item !== "");

function toSession(token: string, payload: JWTPayload): VerifiedSession {
  const { sub, sid, amr, exp, name } = payload;
  if (typeof sub !== "string" || sub === "" || typeof sid !== "string" || sid === "" || !isStringList(amr)) {
    throw new SessionVerifyError("invalid_claims", "claims are missing sub, sid or amr");
  }
  return {
    token,
    userHash: sub,
    sessionId: sid,
    amr,
    expiresAtMs: typeof exp === "number" ? exp * 1000 : null,
    displayName: typeof name === "string" && name.trim() !== "" ? name : null,
  };
}

/** Verify a compact JWS against the Passport's keys; throws `SessionVerifyError` naming why not. */
export async function verifySessionToken(token: string, getKey: JWTVerifyGetKey = sessionJwks()): Promise<VerifiedSession> {
  try {
    const { payload } = await jwtVerify(token, getKey, {
      algorithms: [...SESSION_ALGORITHMS],
      clockTolerance: CLOCK_TOLERANCE_SECONDS,
      /**
       * 🔴 **`typ` IS INSISTED ON FOR THE PASSPORT'S TOKENS AND NOT FOR BETTER AUTH'S** (D84), and
       * the asymmetry is the point rather than a gap.
       *
       * The Passport signs a session, a webhook signature and an OIDC id_token with ONE key and one
       * `kid`, so its signature says "the Passport made this" and nothing about what it made — and an
       * id_token's claims are a superset of a session's, so without this line one read out of a log
       * would be a session for the person it names. Better Auth's key signs exactly one kind of
       * artifact and stamps no `typ` at all, so demanding one refuses every genuine token: a correct
       * password, a 200 from the server, and "Sign-in did not complete" on screen.
       *
       * What replaces it there is stricter, not looser: `iss` and `aud` are both checked, by the key
       * resolver that picked those keys and by the API's own gate (`services/auth/identity-session.ts`).
       * If Better Auth ever signs a second kind of artifact — an OIDC provider plugin — this is the
       * line that has to change first.
       */
      ...(isIdentityToken(token) ? {} : { typ: SESSION_JWT_TYP }),
    });
    return toSession(token, payload);
  } catch (err) {
    if (err instanceof SessionVerifyError) throw err;
    throw rejectionFor(err);
  }
}
